import { createId } from '@paralleldrive/cuid2';
import type { DashboardConfig, BlockWidgetItem } from '../types/dashboard-config.js';
import type { CanonicalWidgetData } from '../types/dashboard-data.js';
import { getEligibilityTerminology } from '../types/hackathon.js';

interface SegmentData {
  id: number;
  name: string;
  size: number;
  pct: number;
  profile_narrative?: string;
  commercial_action?: string;
  gap_analysis?: string | Record<string, unknown>;
  archetype_momentum_mapping?: string;
  key_stats?: Record<string, Record<string, unknown>>;
  distinguishing_features?: { name: string; z_score: number }[];
  pca_drivers?: { name: string; contribution: number }[];
  key_distinguishing_features?: string[];
  top_molecules?: { molecule: string; mean_share: number }[];
  archetype_distribution?: Record<string, number>;
  momentum_distribution?: Record<string, number>;
  feature_means?: Record<string, number | null>;
  feature_z_scores?: Record<string, number | null>;
}

// Trinity color palette
const TRINITY_COLORS = [
  '#28aae1',
  '#565add',
  '#1D94C7',
  '#7b7bc7',
  '#76d1ff',
  '#b3b5e8',
  '#3035d5',
  '#E5F5FC',
];

type Slot = 1 | 2 | 3;
type Span = 1 | 2 | 3;

function w(
  type: 'chart' | 'text' | 'metric_card' | 'segment_overview',
  title: string,
  data: CanonicalWidgetData,
  config: Record<string, unknown> = {},
  source?: string
): BlockWidgetItem['widget'] {
  return {
    id: createId(),
    type,
    config: { title, ...config } as never,
    dataSource: { type: 'static' as const, data },
    ...(source ? { sourceAttribution: { label: source } } : {}),
  };
}

function it(
  row: number,
  slot: Slot,
  span: Span,
  widget: BlockWidgetItem['widget'],
  rowSpan?: number
): BlockWidgetItem {
  return { type: 'widget', row, slot, span, widget, ...(rowSpan ? { rowSpan } : {}) };
}

function pieData(entries: Record<string, number>): CanonicalWidgetData {
  const sorted = Object.entries(entries).sort((a, b) => b[1] - a[1]);
  return {
    type: 'chart',
    data: {
      type: 'pie',
      chart: {
        title: '',
        xAxisTitle: '',
        yAxisTitle: '',
        data: sorted.map(([n, p]) => ({ id: n, label: n, value: Math.round(p * 100) })),
      },
      colors: TRINITY_COLORS,
    },
  };
}

function barData(items: { x: string; y: number }[], xLabel: string, yLabel: string): CanonicalWidgetData {
  return {
    type: 'chart',
    data: {
      type: 'bar',
      chart: { title: '', xAxisTitle: xLabel, yAxisTitle: yLabel, indexBy: 'x', data: items },
      colors: TRINITY_COLORS,
    },
  };
}

function md(content: string): CanonicalWidgetData {
  return { type: 'text', data: { content } };
}

function kpi(values: string[], labels: string[]): CanonicalWidgetData {
  return { type: 'metric_card', data: { values, labels } };
}

// Formats a numeric mean from key_stats as a percentage, handling 0-1 vs 0-100 scaling.
function pctFromMean(v: Record<string, unknown> | undefined, digits = 0): string {
  const m = v?.mean;
  if (m == null || typeof m !== 'number' || !isFinite(m)) return '—';
  // Heuristic: values <= 1 are assumed to be fractions already.
  const pct = m <= 1 ? m * 100 : m;
  return `${pct.toFixed(digits)}%`;
}

function numFromMean(v: Record<string, unknown> | undefined, digits = 0): string {
  const m = v?.mean;
  if (m == null || typeof m !== 'number' || !isFinite(m)) return '—';
  return digits > 0 ? m.toFixed(digits) : Math.round(m).toLocaleString();
}

// Exact widget titles that are rendered with synthetic / placeholder data.
// SegmentProfile matches these titles and blurs the corresponding widget body
// so users can visually distinguish real charts from previews.
export const PREVIEW_WIDGET_TITLES: readonly string[] = [
  'Top 5 Regimens',
  'Regimen Distribution by LOT × Transplant Eligibility',
  'LOT Flow by Regimen',
  'NEW Patients · Top Molecules',
  'NEW Patient Volume · Monthly',
];

export function buildSegmentDashboard(
  seg: SegmentData,
  dataSource?: string,
  totalPatientPopulation?: number,
  options?: { unblurPreviews?: boolean; skipCharacteristicsBlock?: boolean; skipOverviewBlock?: boolean; groupIdentifier?: string }
): DashboardConfig {
  const elig = getEligibilityTerminology(options?.groupIdentifier ?? '');
  // When unblurPreviews is true, append a zero-width space to preview widget
  // titles so they no longer match PREVIEW_WIDGET_TITLES in SegmentProfile —
  // effectively disabling the client-side blur for this saved page version.
  const mark = (t: string): string => (options?.unblurPreviews ? `${t}\u200B` : t);
  const overviewItems: BlockWidgetItem[] = [];
  const characteristicsItems: BlockWidgetItem[] = [];
  const modelItems: BlockWidgetItem[] = [];
  const src = dataSource || 'Segment Insights';
  const aiSrc = 'AI-Generated · Segment Insights';
  // Local row counters — each block has its own row numbering starting at 1.
  let row = 1;
  let items = characteristicsItems;

  // ═══════════════════════════════════════════════════════════════════════
  // BLOCK 0: SEGMENT OVERVIEW
  // ═══════════════════════════════════════════════════════════════════════
  const narrative = seg.profile_narrative as string | undefined;
  const commercialAction = seg.commercial_action as string | undefined;
  const gapAnalysis = seg.gap_analysis as string | Record<string, unknown> | undefined;
  if (narrative || commercialAction || gapAnalysis) {
    overviewItems.push(it(1, 1, 3, w('segment_overview' as never, 'Segment Overview', {
      type: 'segment_overview',
      data: {
        narrative: narrative ?? '',
        commercialAction: commercialAction ?? undefined,
        gapAnalysis: gapAnalysis ?? undefined,
      },
    } as never, {}, src)));
  }

  // ═══════════════════════════════════════════════════════════════════════
  // BLOCK 1: SEGMENT CHARACTERISTICS
  // ═══════════════════════════════════════════════════════════════════════

  // ── Condensed Key Metrics (6 specific fields) ─────────────────────────────
  const ks = seg.key_stats ?? {};
  // % of HCPs: segment size as % of total HCPs (seg.pct is that fraction)
  const pctHcp = typeof seg.pct === 'number' ? `${(seg.pct * 100).toFixed(0)}%` : '—';
  // % of Patient Population: segment's total patients / overall pop (if known)
  const segTotalPatientsMean = ks.total_patients?.mean as number | undefined;
  const segTotalPatients =
    typeof segTotalPatientsMean === 'number' && typeof seg.size === 'number'
      ? segTotalPatientsMean * seg.size
      : undefined;
  const pctPatientPop =
    segTotalPatients != null && totalPatientPopulation && totalPatientPopulation > 0
      ? `${((segTotalPatients / totalPatientPopulation) * 100).toFixed(0)}%`
      : '—';
  const avgAge = numFromMean(ks.avg_age_of_panel, 1);
  const avgNewStarts = pctFromMean(ks.pct_new_starts, 1);
  // Ineligible = 1 - eligible (if eligibility data exists)
  const eligible = ks.pct_transplant_eligible?.mean;
  const pctTie =
    typeof eligible === 'number' && isFinite(eligible)
      ? `${((1 - (eligible <= 1 ? eligible : eligible / 100)) * 100).toFixed(0)}%`
      : '—';
  const l1Conc = pctFromMean(ks.l1_regimen_concentration, 1);

  items.push(
    it(
      row,
      1,
      3,
      w(
        'metric_card',
        'Key Metrics',
        kpi(
          [pctHcp, pctPatientPop, avgAge, avgNewStarts, pctTie, l1Conc],
          [
            '% of HCPs',
            '% of Patient Population',
            'Avg Patient Age',
            'Avg % New Starts',
            `% ${elig.ineligible}`,
            'L1 Concentration',
          ]
        ),
        {},
        src
      )
    )
  );
  row++;

  // ── Archetype Distribution (pie) — full-width ─────────────────────────────
  const hasArchetype = seg.archetype_distribution && Object.keys(seg.archetype_distribution).length > 0;
  if (hasArchetype) {
    items.push(
      it(
        row,
        1,
        3,
        w('chart', 'Archetype Distribution', pieData(seg.archetype_distribution!), { chartType: 'pie' }, src)
      )
    );
    row++;
  }

  // ── Total Patient Volume Insights: Top 5 Molecules + Top 5 Regimens (pies) ──
  const hasMolecules = seg.top_molecules && seg.top_molecules.length > 0;
  if (hasMolecules) {
    const topMols = seg.top_molecules!.slice(0, 5);
    const molPie = Object.fromEntries(topMols.map((m) => [m.molecule, m.mean_share]));
    items.push(
      it(
        row,
        1,
        2,
        w('chart', 'Top 5 Molecules (% of Patient Volume)', pieData(molPie), { chartType: 'pie' }, src)
      )
    );
  } else {
    items.push(
      it(
        row,
        1,
        2,
        w(
          'chart',
          'Top 5 Molecules (% of Patient Volume)',
          pieData({
            'Molecule A': 0.34,
            'Molecule B': 0.27,
            'Molecule C': 0.18,
            'Molecule D': 0.14,
            'Molecule E': 0.07,
          }),
          { chartType: 'pie' },
          src
        )
      )
    );
  }
  // Top 5 Regimens — regimen-level data not in segment payload yet
  items.push(
    it(
      row,
      3,
      1,
      w(
        'chart',
        mark('Top 5 Regimens'),
        pieData({ VRd: 0.31, 'Dara-Rd': 0.24, KRd: 0.19, DVd: 0.15, Rd: 0.11 }),
        { chartType: 'pie' },
        src
      )
    )
  );
  row++;

  // ── Regimen Distribution by LOT × Eligibility (stacked bar placeholder) ───────
  items.push(
    it(
      row,
      1,
      3,
      w(
        'chart',
        mark(`Regimen Distribution by LOT × ${elig.procedureNoun} Eligibility`),
        barData(
          [
            { x: `L1 · ${elig.eligibleShort}`, y: 42 },
            { x: `L1 · ${elig.ineligibleShort}`, y: 58 },
            { x: `L2 · ${elig.eligibleShort}`, y: 48 },
            { x: `L2 · ${elig.ineligibleShort}`, y: 52 },
            { x: `L3 · ${elig.eligibleShort}`, y: 51 },
            { x: `L3 · ${elig.ineligibleShort}`, y: 49 },
          ],
          'Line × Eligibility',
          '% of Regimens'
        ),
        { chartType: 'bar' },
        src
      )
    )
  );
  row++;

  // ── LOT Flow by Regimen (bar placeholder — no sankey widget available) ──
  items.push(
    it(
      row,
      1,
      3,
      w(
        'chart',
        mark('LOT Flow by Regimen'),
        barData(
          [
            { x: 'L1 → L2 · VRd→Dara-Rd', y: 38 },
            { x: 'L1 → L2 · KRd→DVd', y: 24 },
            { x: 'L1 → L2 · VRd→KRd', y: 18 },
            { x: 'L2 → L3 · Dara-Rd→Elo-Pd', y: 14 },
            { x: 'L2 → L3 · DVd→Kd', y: 9 },
            { x: 'L2 → L3 · KRd→IsaKd', y: 7 },
          ],
          'Transition',
          '% of Patients'
        ),
        { chartType: 'bar' },
        src
      )
    )
  );
  row++;

  // ── NEW Patient Insights — top molecules for new starts (placeholder) ───
  items.push(
    it(
      row,
      1,
      2,
      w(
        'chart',
        mark('NEW Patients · Top Molecules'),
        pieData({
          'Molecule A': 0.41,
          'Molecule B': 0.22,
          'Molecule C': 0.16,
          'Molecule D': 0.12,
          'Molecule E': 0.09,
        }),
        { chartType: 'pie' },
        src
      )
    )
  );
  items.push(
    it(
      row,
      3,
      1,
      w(
        'chart',
        mark('NEW Patient Volume · Monthly'),
        barData(
          [
            { x: 'Jan', y: 28 },
            { x: 'Feb', y: 34 },
            { x: 'Mar', y: 41 },
            { x: 'Apr', y: 37 },
            { x: 'May', y: 45 },
            { x: 'Jun', y: 52 },
          ],
          'Month',
          'New Starts'
        ),
        { chartType: 'bar' },
        src
      )
    )
  );
  row++;

  // ── Patient Mix by LOT (real) ─────────────────────────────────────────────
  const fm = seg.feature_means ?? {};
  const patientMixLot = Object.entries(fm)
    .filter(([k, v]) => /^pct_patients_l[1-4]|pct_l[1-4]_patients/i.test(k) && v != null)
    .map(([k, v]) => ({
      x: k.replace(/_/g, ' ').toUpperCase(),
      y: Number(((v as number) * 100).toFixed(1)),
    }));
  if (patientMixLot.length > 0) {
    items.push(
      it(
        row,
        1,
        3,
        w(
          'chart',
          'Patient Mix by Line of Therapy',
          barData(patientMixLot, 'Line', '% of Patients'),
          { chartType: 'bar' },
          src
        )
      )
    );
    row++;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // BLOCK 2: SEGMENTATION MODEL RESULTS
  // ═══════════════════════════════════════════════════════════════════════
  const characteristicsRows = row - 1;
  row = 1;
  items = modelItems;

  // ── Clustering Drivers (PCA) — WHY the segment formed (span 3) ────────────
  const hasPcaDrivers = seg.pca_drivers && seg.pca_drivers.length > 0;
  if (hasPcaDrivers) {
    const pcaItems = seg.pca_drivers!.slice(0, 10).map((f) => ({
      x: f.name.replace(/_/g, ' '),
      y: Number(f.contribution.toFixed(3)),
    }));
    items.push(
      it(
        row,
        1,
        3,
        w(
          'chart',
          'Clustering Drivers (PCA)',
          barData(pcaItems, 'Feature', 'Contribution'),
          { chartType: 'bar' },
          src
        )
      )
    );
    row++;
  }

  // ── Row 3b: Statistical Profile / Z-Scores (span 2) + Archetype/Momentum narrative (span 1) ──
  const hasFeatures = seg.distinguishing_features && seg.distinguishing_features.length > 0;
  const hasArchNarrative = !!seg.archetype_momentum_mapping;

  if (hasFeatures) {
    const featItems = seg.distinguishing_features!.slice(0, 10).map((f) => ({
      x: f.name.replace(/_/g, ' '),
      y: Number(f.z_score.toFixed(2)),
    }));
    items.push(
      it(
        row,
        1,
        2,
        w(
          'chart',
          'Statistical Profile (Z-Scores)',
          barData(featItems, 'Feature', 'Z-Score'),
          { chartType: 'bar' },
          src
        )
      )
    );

    if (hasArchNarrative) {
      items.push(
        it(
          row,
          3,
          1,
          w(
            'text',
            'Archetype & Momentum',
            md(`### Archetype & Momentum\n\n${seg.archetype_momentum_mapping}`),
            { textMode: 'markdown' },
            aiSrc
          )
        )
      );
    }
    row++;
  }

  // ── Row 4: Feature Z-Scores heatmap bar (full width) ──────────────────────
  if (seg.feature_z_scores && Object.keys(seg.feature_z_scores).length > 0) {
    const zEntries = Object.entries(seg.feature_z_scores)
      .filter(([, v]) => v != null && isFinite(v!))
      .map(([k, v]) => ({ name: k, z: v! }))
      .sort((a, b) => Math.abs(b.z) - Math.abs(a.z))
      .slice(0, 15);

    if (zEntries.length > 0) {
      const zItems = zEntries.map((e) => ({
        x: e.name.replace(/_/g, ' '),
        y: Number(e.z.toFixed(2)),
      }));
      items.push(
        it(
          row,
          1,
          3,
          w(
            'chart',
            'Feature Z-Scores vs Population',
            barData(zItems, 'Feature', 'Z-Score'),
            { chartType: 'bar' },
            src
          )
        )
      );
      row++;
    }
  }

  // ── Feature Means (top 15, bar chart, span 2) + Key Features text (span 1) ──
  const hasFeatureMeans = seg.feature_means && Object.keys(seg.feature_means).length > 0;
  const hasKeyFeatures = seg.key_distinguishing_features && seg.key_distinguishing_features.length > 0;

  if (hasFeatureMeans) {
    const meanEntries = Object.entries(seg.feature_means!)
      .filter(([, v]) => v != null && isFinite(v!))
      .map(([k, v]) => ({ name: k, val: v! }))
      .sort((a, b) => Math.abs(b.val) - Math.abs(a.val))
      .slice(0, 15);

    if (meanEntries.length > 0) {
      const span: Span = hasKeyFeatures ? 2 : 3;
      const meanItems = meanEntries.map((e) => ({
        x: e.name.replace(/_/g, ' '),
        y: Number(e.val.toFixed(3)),
      }));
      items.push(
        it(
          row,
          1,
          span,
          w(
            'chart',
            'Feature Averages',
            barData(meanItems, 'Feature', 'Mean Value'),
            { chartType: 'bar' },
            src
          )
        )
      );

      if (hasKeyFeatures) {
        const bullets = seg.key_distinguishing_features!.map((f) => `- ${f}`).join('\n');
        items.push(
          it(
            row,
            3,
            1,
            w(
              'text',
              'Key Features',
              md(`### Key Distinguishing Features\n\n${bullets}`),
              { textMode: 'markdown' },
              aiSrc
            )
          )
        );
      }
      row++;
    }
  } else if (hasKeyFeatures) {
    const bullets = seg.key_distinguishing_features!.map((f) => `- ${f}`).join('\n');
    items.push(
      it(
        row,
        1,
        3,
        w(
          'text',
          'Key Features',
          md(`### Key Distinguishing Features\n\n${bullets}`),
          { textMode: 'markdown' },
          aiSrc
        )
      )
    );
    row++;
  }

  // Commercial Action + Data Gaps are rendered in the overview boxes above the
  // dashboard, so we intentionally omit the redundant widget row here.

  const modelRows = row - 1;
  const blocks = [];

  // Block 0: Segment Overview (narrative + commercial action + data gaps)
  // Skipped when rendered as a standalone React component (market view flow).
  if (!options?.skipOverviewBlock && overviewItems.length > 0) {
    blocks.push({
      id: createId(),
      title: 'Segment Overview',
      w: 3 as const,
      collapsible: true,
      minRows: 1,
      items: overviewItems,
    });
  }

  if (!options?.skipCharacteristicsBlock) {
    // Normal: Characteristics then Model Results
    blocks.push({
      id: createId(),
      title: 'Segment Characteristics',
      w: 3 as const,
      collapsible: true,
      minRows: Math.max(1, characteristicsRows),
      items: characteristicsItems,
    });
  }

  blocks.push({
    id: createId(),
    title: 'Segment Model Results',
    w: 3 as const,
    collapsible: true,
    defaultCollapsed: true,
    minRows: Math.max(1, modelRows),
    items: modelItems,
  });

  return { hideTitle: true, blocks };
}
